import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { DropdownDownIcon } from '../icons/dropdownDown.icon';
import { Flex } from '../grid';

const AccordionStyle = styled.div({
  width: '100%',
})

const AccordionHeaderStyle = styled.div((props: { expandable: boolean }) => ({
  width: '100%',
  cursor: props.expandable ? 'pointer' : 'default'
}))

const AccordionBodyStyle = styled.div((props: { isOpen: boolean, maxHeight: number | null, gap: number | string }) => ({
  // eslint-disable-next-line no-nested-ternary
  maxHeight: props.isOpen ? (props.maxHeight ? `${props.maxHeight}px` : 'auto') : '0px',
  overflow: 'hidden',
  transition: 'max-height 0.3s ease',
  marginTop: props.isOpen ? props.gap : 0,
}))

const FlexItem = styled.div({
  flex: 1
})

const FlexItem2 = styled.div({
  flex: 0,
  alignSelf: 'center',
})

export interface AccordionProps {
  title: string | React.JSX.Element | React.JSX.Element[];
  content: string | React.JSX.Element | React.JSX.Element[];
  key?: number;
  icon?: React.ReactNode;
  expandIcon?: boolean;
  expandable?: boolean;
  open?: boolean;
  gap?: number | string;
  onChange?: () => void;
  expandIconComponent?: React.ReactElement;
  /** When true, content is not rendered until accordion is opened for the first time, then stays mounted */
  lazy?: boolean;
}

export const Accordion = ({ icon, title, content, open = false, expandable = true, expandIcon = true, gap = 0, onChange, expandIconComponent = <DropdownDownIcon />, lazy = false }: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [hasBeenOpened, setHasBeenOpened] = useState(open);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsOpen(open);
    if (open) {
      setHasBeenOpened(true);
    }
  }, [open]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (contentRef.current) {
        setMaxHeight(contentRef.current.scrollHeight);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const toggleAccordion = () => {
    if (!isOpen) {
      setHasBeenOpened(true);
    }
    setIsOpen(!isOpen);
    if (onChange) onChange();
  };

  return (
    <AccordionStyle>
      <AccordionHeaderStyle onClick={() => expandable && toggleAccordion()} expandable={expandable}>
        <Flex justify='space-between'>
          {icon ? <>{icon}</> : <></>}
          <FlexItem>
            {title}
          </FlexItem>
          {expandIcon ? <FlexItem2>{React.cloneElement(expandIconComponent, { isOpen })}</FlexItem2> : <></>}
        </Flex>
      </AccordionHeaderStyle>
      <AccordionBodyStyle isOpen={isOpen} maxHeight={maxHeight} ref={contentRef} gap={gap}>
        {(!lazy || hasBeenOpened) && content}
      </AccordionBodyStyle>
    </AccordionStyle>
  );
};
